import React, { useMemo, useRef, useState, useEffect } from "react";
import { ColDef, GridApi } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { toast } from "react-toastify";
import { Icon } from "@iconify/react";
import {
  Box,
  VStack,
  HStack,
  ButtonPrimary,
  ButtonPrimary1,
} from "../../../components/utils";
import SearchInput from "../../../components/shared/SearchInput";
import { formatDisplayDate } from "../../../utils/date";
import { BlackScholesV2, BlackScholes } from "../../../types/blackScholes";
import { useBlackScholesData } from "../../../queries/blackScholes";

interface PreviewAssumptionsProps {
  grantScopeData: BlackScholesV2 | undefined;
  onBack: () => void;
  onNext: () => void;
  setGrantScopeData: React.Dispatch<
    React.SetStateAction<BlackScholesV2 | undefined>
  >;
}

export const PreviewAssumptions: React.FC<PreviewAssumptionsProps> = ({
  grantScopeData,
  onBack,
  onNext,
  setGrantScopeData,
}) => {
  const gridApi = useRef<GridApi | null>(null);
  const [searchQuery, setSearchQuery] = useState("");

  const { mutate: blackScholesMutation, isLoading } = useBlackScholesData();

  const rowData = useMemo(() => {
    if (!grantScopeData?.filteredGrants || !grantScopeData?.grantDatesWithData)
      return [];

    const grouped: { [key: string]: any } = {};

    grantScopeData.filteredGrants.forEach((grant) => {
      const grantDate = grant.dateOfGrant as unknown as string;

      if (!grouped[grantDate]) {
        const assumptions = grantScopeData.grantDatesWithData[
          grantDate
        ] as BlackScholes;
        grouped[grantDate] = {
          grantDate,
          totalGrants: 0,
          valuationDate: assumptions?.valuationDate,
          volatility: assumptions?.volatility,
          riskFreeRate: assumptions?.riskFreeRate,
          stockPrice: assumptions?.stockPrice,
          dividendYield: assumptions?.dividendYield,
        };
      }
      grouped[grantDate].totalGrants += 1;
    });

    return Object.values(grouped).sort(
      (a: any, b: any) =>
        new Date(a.grantDate).getTime() - new Date(b.grantDate).getTime()
    );
  }, [grantScopeData]);

  const defaultColDef = useMemo<ColDef>(
    () => ({
      sortable: true,
      wrapText: true,
      flex: 1,
      autoHeight: true,
      initialWidth: 150,
      wrapHeaderText: true,
      autoHeaderHeight: true,
      cellClass: "multiline",
      columnsMenuParams: {
        suppressColumnFilter: true,
      },
      filterParams: {
        buttons: [],
      },
      menuTabs: [],
      minWidth: 150,
      filter: true,
      resizable: true,
    }),
    []
  );

  const columnDefs: ColDef[] = useMemo(
    () => [
      {
        headerName: "Grant Date".toUpperCase(),
        field: "grantDate",
        valueFormatter: (params) => formatDisplayDate(params.value),
        getQuickFilterText: (params) => formatDisplayDate(params.value),
        minWidth: 150,
      },
      {
        headerName: "Total Grants".toUpperCase(),
        field: "totalGrants",
        minWidth: 120,
      },
      {
        headerName: "Valuation Date".toUpperCase(),
        field: "valuationDate",
        valueFormatter: (params) =>
          params.value ? formatDisplayDate(params.value) : "-",
        getQuickFilterText: (params) =>
          params.value ? formatDisplayDate(params.value) : "-",
        minWidth: 150,
      },
      {
        headerName: "Volatility (%)".toUpperCase(),
        field: "volatility",
        valueFormatter: (params) => params.value ?? "-",
      },
      {
        headerName: "Risk-Free Rate (%)".toUpperCase(),
        field: "riskFreeRate",
        valueFormatter: (params) => params.value ?? "-",
      },
      {
        headerName: "Stock Price".toUpperCase(),
        field: "stockPrice",
        valueFormatter: (params) => params.value ?? "-",
      },
      {
        headerName: "Dividend Yield (%)".toUpperCase(),
        field: "dividendYield",
        valueFormatter: (params) => params.value ?? "-",
      },
    ],
    []
  );

  const SummaryCard = ({
    label,
    value,
    icon,
  }: {
    label: string;
    value: string | number;
    icon: string;
  }) => (
    <Box className="flex-1 bg-gray-50 p-4 rounded-lg border border-gray-200 flex items-center gap-4">
      <div className="p-3 bg-white rounded-full shadow-sm border border-gray-200">
        <Icon icon={icon} width={24} height={24} />
      </div>
      <div>
        <p className="text-xs text-gray-500 font-medium uppercase tracking-wider">
          {label}
        </p>
        <p className="text-lg font-bold text-gray-900">{value}</p>
      </div>
    </Box>
  );

  const onGridReady = (params: any) => {
    gridApi.current = params.api;
  };

  useEffect(() => {
    if (gridApi.current) {
      gridApi.current.setQuickFilter(searchQuery);
    }
  }, [searchQuery]);

  const handleSubmit = () => {
    if (!grantScopeData) return;
    blackScholesMutation(grantScopeData, {
      onSuccess: (data) => {
        setGrantScopeData((prev) =>
          prev ? { ...prev, calculatedData: data } : prev
        );
        onNext();
      },
      onError: (error) => {
        toast("Error calculating valuation. Please try again.", {
          type: "error",
        });
      },
    });
  };

  const totalGrants = grantScopeData?.filteredGrants?.length || 0;
  const uniqueDates = Object.keys(
    grantScopeData?.grantDatesWithData || {}
  ).length;
  const dateRange =
    grantScopeData?.startDate && grantScopeData?.endDate
      ? `${formatDisplayDate(grantScopeData.startDate)} - ${formatDisplayDate(
          grantScopeData.endDate
        )}`
      : "-";

  return (
    <Box className="w-full">
      <VStack className="">
        <VStack className="justify-between gap-1 pb-3 pl-6 font-medium border-b-[0.5px] pt-7">
          <HStack className="flex justify-between font-semibold text-lg1 text-black-501">
            Review & Calculate
          </HStack>
          <p className="text-gray-401 font-medium text-sm3">
            Verify your assumptions below. This data will be used to generate
            the final valuation report.
          </p>
        </VStack>

        <VStack className="gap-6 w-full p-6">
          <HStack className="gap-6 w-full items-end justify-between">
            <Box className="flex-1 max-w-sm">
              <SearchInput
                placeholder="Search grant dates..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </Box>
          </HStack>

          <HStack className="gap-4 w-full">
            <SummaryCard
              label="Total Grants Selected"
              value={totalGrants}
              icon="mdi:file-document-multiple-outline"
            />
            <SummaryCard
              label="Valuation Groups"
              value={uniqueDates}
              icon="mdi:calendar-multiselect"
            />
            <SummaryCard
              label="Period Covered"
              value={dateRange}
              icon="mdi:calendar-range"
            />
          </HStack>

          <HStack className="justify-between w-full">
            <Box
              style={{
                height: `${
                  (rowData.length >= 10 ? 10 : rowData.length + 3) * 60
                }px`,
              }}
              className="w-full h-full max-h-full overflow-x-auto ag-theme-material"
            >
              <AgGridReact
                rowData={rowData}
                columnDefs={columnDefs}
                onGridReady={onGridReady}
                defaultColDef={defaultColDef}
                suppressRowTransform={true}
                animateRows={true}
                pagination={false}
                rowClass={
                  "border-t border-dashed cursor-pointer hover:bg-slate-50 "
                }
                overlayNoRowsTemplate={
                  '<span style="padding: 10px; border: 2px solid #444; background: lightgoldenrodyellow;   margin-top: 50px;">No Rows To Show</span>'
                }
              />
            </Box>
          </HStack>

          <HStack className="justify-between pt-4 border-t border-gray-200 mt-4">
            <ButtonPrimary1 onClick={onBack}>Back</ButtonPrimary1>
            <ButtonPrimary onClick={handleSubmit} loading={isLoading}>
              Calculate Valuation
            </ButtonPrimary>
          </HStack>
        </VStack>
      </VStack>
    </Box>
  );
};
